import { useRef, useState } from 'react';

import { ApiError } from '@/api/client';
import {
  useApprove,
  useComponents,
  useQueue,
  useRedecide,
  useReject,
  useRetry,
} from '@/api/queries';
import { proposalPath } from '@/app/scope';
import { useToast } from '@/app/toasts';
import { Button, LinkButton } from '@/components/Button';
import { HealthBadge, ProposalStatusBadge } from '@/components/badges';
import { Dialog } from '@/components/Dialog';
import { Checkbox, Field, Textarea } from '@/components/fields';
import { useFocusFirstInvalid } from '@/components/focus';
import { ArrowRight } from '@/components/icons';
import { Kv, Notice } from '@/components/primitives';
import { Timestamp } from '@/components/Timestamp';
import { HEALTH_LABEL } from '@/domain/health';
import type { Proposal, PublishResult } from '@/domain/types';

import { ApproverField, DecisionFields, ErrorSummary } from './decision';
import { emptyDecision, explainError, useApproverName, type DecisionValues } from './decisionState';

/** What every decision dialog reports back so the caller can show the right follow-up. */
export type DecisionOutcome =
  | { kind: 'published'; result: PublishResult }
  | { kind: 'rejected'; proposal: Proposal }
  | { kind: 'publish_failed'; proposal: Proposal; lastError: string }
  | { kind: 'conflict'; current: Proposal };

interface CommonProps {
  proposal: Proposal;
  displayName: string;
  open: boolean;
  onClose: () => void;
  onDone: (outcome: DecisionOutcome) => void;
}

function decisionBody(values: DecisionValues) {
  return {
    incident_action: values.incident_action,
    ...(values.incident_title.trim() ? { incident_title: values.incident_title.trim() } : {}),
    ...(values.incident_body.trim() ? { incident_body: values.incident_body.trim() } : {}),
    ...(values.resolution_body.trim() ? { resolution_body: values.resolution_body.trim() } : {}),
  };
}

/**
 * "from → to" as one unit. Inline, it moves to the next line whole so a sentence around it
 * never breaks before its full stop; stacked, it fits a narrow box without splitting the arrow
 * from its badges.
 */
function ChangeLine({ proposal, stacked = false }: { proposal: Proposal; stacked?: boolean }) {
  if (stacked) {
    return (
      <span className="inline-flex flex-col items-start gap-1">
        <HealthBadge health={proposal.from_status} />
        <ArrowRight size={14} className="text-ink-3 rotate-90" />
        <HealthBadge health={proposal.to_status} />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 align-middle whitespace-nowrap">
      <HealthBadge health={proposal.from_status} />
      <ArrowRight size={14} className="text-ink-3" />
      <HealthBadge health={proposal.to_status} />
    </span>
  );
}

/** Approve (TDD §5.4): the incident decision, optional co-approvals, then publish and wait. */
export function ApproveDialog({ proposal, displayName, open, onClose, onDone }: CommonProps) {
  const [decision, setDecision] = useState<DecisionValues>(emptyDecision());
  const [include, setInclude] = useState<Set<string>>(new Set());
  const approver = useApproverName();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const mutation = useApprove(proposal.scope);
  const queue = useQueue(proposal.scope);
  const components = useComponents(proposal.scope);
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, fieldErrors);

  const others = (queue.data ?? []).filter(
    (p) => p.proposal_id !== proposal.proposal_id && p.status === 'open' && p.to_status !== 'ok',
  );
  const displayNames = new Map(
    (components.data?.components ?? []).map((c) => [c.config.capability_id, c.config.display_name]),
  );

  const submit = async () => {
    setFieldErrors({});
    setMessage(null);
    if (!approver.name.trim()) {
      setFieldErrors({ approver: 'Enter your name.' });
      return;
    }
    approver.remember();
    try {
      const result = await mutation.mutateAsync({
        id: proposal.proposal_id,
        body: {
          version: proposal.version,
          approver: approver.name.trim(),
          ...decisionBody(decision),
          ...(decision.incident_action === 'open' && include.size > 0
            ? {
                include: others
                  .filter((p) => include.has(p.proposal_id))
                  .map((p) => ({ id: p.proposal_id, version: p.version })),
              }
            : {}),
        },
      });
      toast.push({
        tone: 'success',
        title: `Published: ${displayName} now shows ${HEALTH_LABEL[result.proposal.to_status]}`,
        ...(result.proposal.published_link
          ? { description: `Incident: ${result.proposal.published_link}` }
          : {}),
      });
      onDone({ kind: 'published', result });
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.conflict) {
          onDone({ kind: 'conflict', current: error.conflict.current as Proposal });
          return;
        }
        if (error.publishFailed) {
          const failed = error.publishFailed;
          toast.push({
            tone: 'error',
            title: `The publish failed: ${failed.last_error}`,
            description: 'The proposal stays in the queue with Retry now.',
          });
          onDone({
            kind: 'publish_failed',
            proposal: failed.proposal,
            lastError: failed.last_error,
          });
          return;
        }
      }
      const explained = explainError(error);
      setFieldErrors(explained.fields);
      setMessage(explained.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Approve: ${displayName}`}
      description={
        <>
          The page changes from <ChangeLine proposal={proposal} /> as soon as you approve.
        </>
      }
      size="lg"
      busy={mutation.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={mutation.isPending}>
            Approve and publish
          </Button>
        </>
      }
    >
      <form
        ref={formRef}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <DecisionFields
          scope={proposal.scope}
          values={decision}
          onChange={setDecision}
          errors={fieldErrors}
          disabled={mutation.isPending}
        />

        {decision.incident_action === 'open' && others.length > 0 ? (
          <fieldset className="m-0 p-0 border-0">
            <legend className="text-body font-medium text-ink mb-2">
              Also include in this incident
            </legend>
            <div className="flex flex-col gap-1.5">
              {others.map((p) => (
                <Checkbox
                  key={p.proposal_id}
                  label={`${displayNames.get(p.capability_id) ?? p.capability_id}: ${HEALTH_LABEL[p.from_status]} → ${HEALTH_LABEL[p.to_status]}`}
                  checked={include.has(p.proposal_id)}
                  disabled={mutation.isPending}
                  onChange={(e) => {
                    const next = new Set(include);
                    if (e.target.checked) next.add(p.proposal_id);
                    else next.delete(p.proposal_id);
                    setInclude(next);
                  }}
                />
              ))}
            </div>
          </fieldset>
        ) : null}

        <ApproverField
          label="Approved by"
          value={approver.name}
          onChange={approver.setName}
          error={fieldErrors.approver}
          disabled={mutation.isPending}
        />
        <ErrorSummary message={message} />
      </form>
    </Dialog>
  );
}

/** Reject (TDD §5.4): a mandatory reason; nothing reaches the page. */
export function RejectDialog({ proposal, displayName, open, onClose, onDone }: CommonProps) {
  const [reason, setReason] = useState('');
  const approver = useApproverName();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const mutation = useReject(proposal.scope);
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, fieldErrors);

  const submit = async () => {
    setFieldErrors({});
    setMessage(null);
    const local: Record<string, string> = {};
    if (!reason.trim()) local.reason = 'Give a reason.';
    if (!approver.name.trim()) local.approver = 'Enter your name.';
    if (Object.keys(local).length > 0) {
      setFieldErrors(local);
      return;
    }
    approver.remember();
    try {
      const result = await mutation.mutateAsync({
        id: proposal.proposal_id,
        body: { version: proposal.version, approver: approver.name.trim(), reason: reason.trim() },
      });
      toast.push({
        tone: 'success',
        title: `Rejected: ${displayName} stays ${HEALTH_LABEL[proposal.from_status]}`,
      });
      onDone({ kind: 'rejected', proposal: result.proposal });
    } catch (error) {
      if (error instanceof ApiError && error.conflict) {
        onDone({ kind: 'conflict', current: error.conflict.current as Proposal });
        return;
      }
      const explained = explainError(error);
      setFieldErrors(explained.fields);
      setMessage(explained.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Reject: ${displayName}`}
      description={
        <>
          <ChangeLine proposal={proposal} /> is refused; nothing is written to the page.
        </>
      }
      busy={mutation.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={submit} loading={mutation.isPending}>
            Reject proposal
          </Button>
        </>
      }
    >
      <form
        ref={formRef}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Reason" required error={fieldErrors.reason}>
          {(props) => (
            <Textarea
              {...props}
              name="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Known test-data reset; the service itself is fine."
              disabled={mutation.isPending}
            />
          )}
        </Field>
        <ApproverField
          label="Rejected by"
          value={approver.name}
          onChange={approver.setName}
          error={fieldErrors.approver}
          disabled={mutation.isPending}
        />
        <ErrorSummary message={message} />
      </form>
    </Dialog>
  );
}

/** Redecide (TDD §5.4): a new incident decision for a failed publish, then publish and wait. */
export function RedecideDialog({ proposal, displayName, open, onClose, onDone }: CommonProps) {
  const [decision, setDecision] = useState<DecisionValues>(() => ({
    incident_action:
      proposal.incident_action === 'attach' || proposal.incident_action === 'open'
        ? proposal.incident_action
        : 'none',
    incident_title: proposal.incident_title ?? '',
    incident_body: proposal.incident_body ?? '',
    resolution_body: proposal.resolution_body ?? '',
  }));
  const approver = useApproverName();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const mutation = useRedecide(proposal.scope);
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, fieldErrors);

  const submit = async () => {
    setFieldErrors({});
    setMessage(null);
    if (!approver.name.trim()) {
      setFieldErrors({ approver: 'Enter your name.' });
      return;
    }
    approver.remember();
    try {
      const result = await mutation.mutateAsync({
        id: proposal.proposal_id,
        body: {
          version: proposal.version,
          approver: approver.name.trim(),
          ...decisionBody(decision),
        },
      });
      toast.push({
        tone: 'success',
        title: `Published: ${displayName} now shows ${HEALTH_LABEL[result.proposal.to_status]}`,
      });
      onDone({ kind: 'published', result });
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.conflict) {
          onDone({ kind: 'conflict', current: error.conflict.current as Proposal });
          return;
        }
        if (error.publishFailed) {
          const failed = error.publishFailed;
          toast.push({ tone: 'error', title: `The publish failed again: ${failed.last_error}` });
          onDone({
            kind: 'publish_failed',
            proposal: failed.proposal,
            lastError: failed.last_error,
          });
          return;
        }
      }
      const explained = explainError(error);
      setFieldErrors(explained.fields);
      setMessage(explained.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Decide again: ${displayName}`}
      description="Choose the incident decision again, then publish."
      size="lg"
      busy={mutation.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={mutation.isPending}>
            Publish with this decision
          </Button>
        </>
      }
    >
      <form
        ref={formRef}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {proposal.last_error ? (
          <Notice tone="danger">Last error: {proposal.last_error}</Notice>
        ) : null}
        <DecisionFields
          scope={proposal.scope}
          values={decision}
          onChange={setDecision}
          errors={fieldErrors}
          disabled={mutation.isPending}
        />
        <ApproverField
          label="Decided by"
          value={approver.name}
          onChange={approver.setName}
          error={fieldErrors.approver}
          disabled={mutation.isPending}
        />
        <ErrorSummary message={message} />
      </form>
    </Dialog>
  );
}

/** Retry now (TDD §5.4, §6.7): the same command again, recorded as requested by the person. */
export function RetryDialog({ proposal, displayName, open, onClose, onDone }: CommonProps) {
  const approver = useApproverName();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const mutation = useRetry(proposal.scope);
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, fieldErrors);

  const submit = async () => {
    setFieldErrors({});
    setMessage(null);
    if (!approver.name.trim()) {
      setFieldErrors({ approver: 'Enter your name.' });
      return;
    }
    approver.remember();
    try {
      const result = await mutation.mutateAsync({
        id: proposal.proposal_id,
        body: { version: proposal.version, approver: approver.name.trim() },
      });
      toast.push({
        tone: 'success',
        title: `Published: ${displayName} now shows ${HEALTH_LABEL[result.proposal.to_status]}`,
      });
      onDone({ kind: 'published', result });
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.conflict) {
          onDone({ kind: 'conflict', current: error.conflict.current as Proposal });
          return;
        }
        if (error.publishFailed) {
          const failed = error.publishFailed;
          toast.push({ tone: 'error', title: `The publish failed again: ${failed.last_error}` });
          onDone({
            kind: 'publish_failed',
            proposal: failed.proposal,
            lastError: failed.last_error,
          });
          return;
        }
      }
      const explained = explainError(error);
      setFieldErrors(explained.fields);
      setMessage(explained.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Retry now: ${displayName}`}
      description="Sends the same command to the status page again. Safe to repeat."
      busy={mutation.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={mutation.isPending}>
            Retry the publish
          </Button>
        </>
      }
    >
      <form
        ref={formRef}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Kv
          items={[
            { label: 'Change', value: <ChangeLine proposal={proposal} /> },
            { label: 'Last error', value: proposal.last_error ?? '—' },
            ...(proposal.approved_at
              ? [{ label: 'Approved', value: <Timestamp iso={proposal.approved_at} mode="full" /> }]
              : []),
          ]}
        />
        <ApproverField
          label="Requested by"
          value={approver.name}
          onChange={approver.setName}
          error={fieldErrors.approver}
          disabled={mutation.isPending}
        />
        <ErrorSummary message={message} />
      </form>
    </Dialog>
  );
}

/**
 * The 409 path (TDD §5.6, §5.7): the proposal changed under the person. Show what changed
 * and never resubmit; the person decides on the new situation.
 */
export function ConflictDialog({
  before,
  current,
  displayName,
  open,
  onClose,
}: {
  before: Proposal;
  current: Proposal;
  displayName: string;
  open: boolean;
  onClose: () => void;
}) {
  const same = before.proposal_id === current.proposal_id;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="This proposal changed while you were deciding"
      description={`Nothing was submitted for ${displayName}. Look at the new situation before deciding again.`}
      footer={
        <>
          {current.superseded_by ? (
            <LinkButton
              to={proposalPath(current.superseded_by)}
              variant="primary"
              onClick={onClose}
            >
              Open the new proposal
            </LinkButton>
          ) : null}
          <Button variant={current.superseded_by ? 'secondary' : 'primary'} onClick={onClose}>
            Got it
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2 text-body">
        <div className="rounded-sm border border-line p-3">
          <p className="text-ink-3 mb-2">You were deciding on</p>
          <div className="flex flex-col gap-2">
            <ChangeLine proposal={before} stacked />
            <ProposalStatusBadge status={before.status} />
            <span className="text-ink-3 tabular">version {before.version}</span>
          </div>
        </div>
        <div className="rounded-sm border border-brand/40 bg-brand-soft p-3">
          <p className="text-ink-3 mb-2">It is now</p>
          <div className="flex flex-col gap-2">
            <ChangeLine proposal={current} stacked />
            <ProposalStatusBadge status={current.status} />
            {current.withdrawn_reason ? (
              <span className="text-ink-2">
                Withdrawn: {current.withdrawn_reason.replaceAll('_', ' ')}
              </span>
            ) : null}
            {current.rejected_by ? (
              <span className="text-ink-2">Rejected by {current.rejected_by}</span>
            ) : null}
            {current.approved_by && current.status !== 'open' ? (
              <span className="text-ink-2">Approved by {current.approved_by}</span>
            ) : null}
            {current.superseded_by ? (
              <span className="text-ink-2">Superseded by a newer proposal</span>
            ) : null}
            <span className="text-ink-3 tabular">
              version {current.version}
              {same ? '' : ' · different proposal'}
            </span>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
